import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  AUTH_PERMISSIONS,
  PermissionCheck,
  PermissionService,
} from 'src/app/report-system/core/services/permission.service';
import {
  buildNavbarMenuItems,
  buildNavbarNotificationConfig,
  buildProfileMenuItems,
  hasAnyRole,
  NavbarMenuItem,
  NavbarNotificationConfig,
} from '../shared-navbar/navbar-config';
import { executeNavbarAction } from '../shared-navbar/navbar-actions';
import { UserManagementService } from '../user-management/user-management.service';

@Component({
  selector: 'app-shared-user-nav',
  templateUrl: './shared-user-nav.component.html',
  standalone: false,
})
export class SharedUserNavComponent implements OnInit, OnChanges, OnDestroy {
  private static readonly ROLE_ACCESS = {
    sharedNav: ['2', '7', '8', '11', '13', '14', '15'],
    adminNav: ['2', '7', '8', '11', '15'],
    siteUserNav: ['13'],
    multiSiteNav: ['14'],
  };
  private static readonly SYSTEM_ADMIN_EXCEPTION_PERMISSIONS: PermissionCheck[] = [
    AUTH_PERMISSIONS['system-admin-corporation'],
    AUTH_PERMISSIONS['system-admin-freesites'],
  ];

  @Input() userRole: number | null = null;
  @Input() currentUrl = '';

  @ViewChild('siteEditStatusDialog') siteEditStatusDialog: any;

  public user: any;
  public siteinfo: any;
  sitename = '';
  allocatedSite: any;
  currentSite = '';
  visitedUserManagementSites = false;
  avatarUrl = './assets/images/avathar.jpg';
  imageSrc: SafeUrl | string = this.avatarUrl;
  menuItemsData: NavbarMenuItem[] = [];
  profileMenuItemsData: NavbarMenuItem[] = [];
  brandRouteValue = '/';
  showSiteNameValue = true;
  showNotificationsValue = false;
  navbarClassValue = 'navbar navbar-expand-lg bg-custom boxshadow fixed-top';
  notificationPermissionValue: NavbarNotificationConfig['permission'] = null;

  private subscriptions: Subscription[] = [];

  constructor(
    private router: Router,
    private userService: UserManagementService,
    private modalService: NgbModal,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    this.user = this.userService.getUserDetail();
    this.siteinfo = this.userService.getSiteDetail();
    this.currentSite = this.userService.getCurrentSite();
    this.visitedUserManagementSites =
      sessionStorage.getItem('visitedUserManagementSites') === 'true';

    if (this.isMultiSiteRole) {
      this.allocatedSite = this.currentSite;
      this.sitename = this.userService.getCurrentSitename() || '';
    } else if (this.siteinfo?.length) {
      this.allocatedSite = this.siteinfo[0].site_id;
      this.sitename = this.siteinfo[0].site_name;
    }

    if (this.isAdminRole && (this.user?.id || this.user?.user_id)) {
      this.loadUserImage(this.user.id || this.user.user_id);
    }

    this.refreshNavConfig();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userRole'] || changes['currentUrl']) {
      this.refreshNavConfig();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  get showNav(): boolean {
    return true;
  }

  get isAdminRole(): boolean {
    return (
      this.hasRoleAccess(SharedUserNavComponent.ROLE_ACCESS.adminNav) ||
      this.permissionService.hasAllPermissions(
        SharedUserNavComponent.SYSTEM_ADMIN_EXCEPTION_PERMISSIONS
      )
    );
  }

  get isSiteUserRole(): boolean {
    return this.hasRoleAccess(SharedUserNavComponent.ROLE_ACCESS.siteUserNav);
  }

  get isMultiSiteRole(): boolean {
    return this.hasRoleAccess(SharedUserNavComponent.ROLE_ACCESS.multiSiteNav);
  }

  get brandRoute(): string {
    return this.brandRouteValue;
  }

  get showSiteName(): boolean {
    return this.showSiteNameValue;
  }

  get showNotifications(): boolean {
    return this.showNotificationsValue;
  }

  get navbarClass(): string {
    return this.navbarClassValue;
  }

  get notificationPermission(): NavbarNotificationConfig['permission'] {
    return this.notificationPermissionValue;
  }

  get avatarSrc(): SafeUrl | string {
    return this.isAdminRole ? this.imageSrc || this.avatarUrl : this.avatarUrl;
  }

  get menuItems(): NavbarMenuItem[] {
    return this.menuItemsData;
  }

  get profileMenuItems(): NavbarMenuItem[] {
    return this.profileMenuItemsData;
  }

  onNavbarAction(actionId: string): void {
    void executeNavbarAction(actionId, {
      router: this.router,
      userService: this.userService,
      clearFilterSelections: () => this.clearAllFilterSelections(),
      allocatedSite: this.allocatedSite,
      user: this.user,
      http: this.http,
      modalService: this.modalService,
      siteEditStatusDialog: this.siteEditStatusDialog,
    });
  }

  private hasRoleAccess(roles: string[]): boolean {
    return hasAnyRole(this.userRole ?? this.user?.role ?? this.user?.user_role, roles);
  }

  private refreshNavConfig(): void {
    this.brandRouteValue = this.isSiteUserRole ? '/reportlibrary/editreport/process' : '/';
    this.showSiteNameValue = !this.isMultiSiteRole || this.currentUrl !== '/';
    const notificationConfig = buildNavbarNotificationConfig(
      this.navbarVariant,
      {
        userRole: this.userRole ?? this.user?.role ?? this.user?.user_role,
      },
    );
    this.showNotificationsValue = notificationConfig.show;
    this.notificationPermissionValue = notificationConfig.permission;
    this.navbarClassValue = this.isAdminRole
      ? 'navbar navbar-expand-lg fixed-top'
      : 'navbar navbar-expand-lg bg-custom boxshadow fixed-top';
    this.menuItemsData = buildNavbarMenuItems(this.navbarVariant, {
      userRole: this.userRole ?? this.user?.role ?? this.user?.user_role,
      allocatedSite: this.allocatedSite,
      hasPfd: !!this.siteinfo?.[0]?.sitepfd,
      showPrimaryMenus: !this.isMultiSiteRole || this.currentUrl !== '/',
    });
    this.profileMenuItemsData = buildProfileMenuItems(this.navbarVariant, {
      visitedUserManagementSites: this.visitedUserManagementSites,
    });
  }

  private get navbarVariant(): 'site-admin' | 'site-user' | 'multisite-user' {
    if (this.isAdminRole) {
      return 'site-admin';
    }

    return this.isMultiSiteRole ? 'multisite-user' : 'site-user';
  }

  private loadUserImage(userId: string | number): void {
    if (!userId) {
      this.imageSrc = this.avatarUrl;
      return;
    }

    const imageUrl = `${environment.nodeUrl1}user-image/${userId}?t=${Date.now()}`;
    const imageSub = this.http
      .get(imageUrl, { responseType: 'blob' })
      .subscribe(
        (blob) => {
          this.imageSrc = this.sanitizer.bypassSecurityTrustUrl(
            URL.createObjectURL(blob)
          );
          this.cdr.detectChanges();
        },
        () => {
          this.imageSrc = this.avatarUrl;
        }
      );
    this.subscriptions.push(imageSub);
  }

  private clearAllFilterSelections(): void {
    try {
      const allKeys = Object.keys(localStorage);
      const filterKeys = allKeys.filter((key) => key.startsWith('reportFilter_'));
      filterKeys.forEach((key) => localStorage.removeItem(key));
    } catch (e) {
      console.error('Error clearing filter selections in shared-user-nav:', e);
    }
  }

}
